<template>
  <div id="app">
    <!-- 页头组件 - 根据路由元信息控制显示 -->
    <AppHeader v-if="showHeader" />
    
    <!-- 主要内容区域 -->
    <main :class="['min-h-screen', showHeader ? 'header-offset py-8' : '']">
      <router-view />
    </main>
    
    <!-- 页脚组件 -->
    <AppFooter v-if="showHeader" />
    
    <!-- AI助手浮窗 -->
    <AiWidget />
    
    <!-- Token调试组件（仅开发环境） -->
    <TokenDebug />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import AiWidget from '@/components/AiWidget.vue'
import TokenDebug from '@/components/TokenDebug.vue'
import authService from '@/services/authService'
import urlUtils from '@/utils/urlUtils'

export default {
  name: 'App',
  components: {
    AppHeader,
    AppFooter,
    AiWidget,
    TokenDebug
  },
  created() {
    // 初始化token迁移（从cookie到localStorage）
    authService.initializeToken();
    
    // 检查是否是登录回调
    if (urlUtils.isLoginCallback()) {
      const token = urlUtils.handleLoginCallback();
      if (token) {
        authService.setToken(token);
        // 跳转到首页
        this.$router.push('/');
      }
    }
    
    // 开发环境跳过认证检查
    if (process.env.NODE_ENV === 'development') {
      console.log('开发环境：跳过App.vue认证检查')
      return
    }
    
    // 检查当前路由是否需要认证
    // 如果路由设置了 requiresAuth: false，则跳过认证检查
    const currentRoute = this.$route;
    const requiresAuth = currentRoute.matched.some(record => record.meta.requiresAuth);
    
    // 只有当路由需要认证时才检查登录状态
    if (requiresAuth) {
      this.checkAuthStatus();
    }
  },
  computed: {
    /**
     * 根据当前路由决定是否显示页头
     */
    showHeader() {
      // 获取当前路由的元信息
      const currentRoute = this.$route;
      // 如果路由元信息中有 hideHeader 字段且为 true，则隐藏页头
      return !(currentRoute.meta && currentRoute.meta.hideHeader === true);
    }
  },
  methods: {
    /**
     * 检查认证状态
     */
    async checkAuthStatus() {
      try {
        const isLoggedIn = await authService.checkLoginStatus();
        if (!isLoggedIn) {
          // 如果未登录，跳转到登录页
          authService.redirectToLogin();
        }
      } catch (error) {
        console.error('认证状态检查失败:', error);
        // 如果检查失败，也跳转到登录页
        authService.redirectToLogin();
      }
    }
  }
}
</script>

<style>
#app {
  font-family: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #000000;
}

/* 全局样式 */
* {
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
  background-color: #f5f5f5;
}

/* 自定义滚动条 */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .max-w-7xl {
    max-width: 100%;
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  .space-x-4 > * + * {
    margin-left: 0.5rem;
  }
}

/* 动画效果 */
.transition-all {
  transition: all 0.3s ease;
}

.duration-300 {
  transition-duration: 300ms;
}

/* 焦点样式 */
.focus\:outline-none:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
}

.focus\:border-purple-500:focus {
  border-color: #8b5cf6;
}

/* 悬停效果 */
.hover\:text-gray-600:hover {
  color: #4b5563;
}

.hover\:text-purple-500:hover {
  color: #8b5cf6;
}

/* 工具类 */
.appearance-none {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.pointer-events-none {
  pointer-events: none;
}

.inset-y-0 {
  top: 0;
  bottom: 0;
}

.right-0 {
  right: 0;
}

.pr-2 {
  padding-right: 0.5rem;
}

.pr-8 {
  padding-right: 2rem;
}

.text-xs {
  font-size: 0.75rem;
}

.text-lg {
  font-size: 1.125rem;
}

.absolute {
  position: absolute;
}

.relative {
  position: relative;
}

.flex-shrink-0 {
  flex-shrink: 0;
}

.z-50 {
  z-index: 50;
}

.transform {
  transform: translateZ(0);
}

.translate-x-full {
  transform: translateX(100%);
}

.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.bg-green-500 {
  background-color: #10b981;
}

.text-white {
  color: #ffffff;
}

.px-6 {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.py-3 {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.rounded-lg {
  border-radius: 0.5rem;
}

.top-4 {
  top: 1rem;
}

.right-4 {
  right: 1rem;
}

.fixed {
  position: fixed;
}
.py-4 {
  padding-top: 75px;
}
.py-8 {
  padding-top: 75px;
  padding-bottom: 2.5rem;
}
</style>
